import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { useSendStore } from '@/store/useSendStore'
import { useGetOnRampPricePerUnit } from '@/domain'
import { useGetWalletDataByCryptoCode } from '@/hooks/useGetWalletDataByCryptoCode'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { currencyUtils } from '@/utils'
import type { CryptoCode, FiatCode } from '@/constants/currencyCodes'

const { cryptoToFiat, fiatToCrypto, parseAmountToFloat } = currencyUtils

export const SendNumberPad: React.FC = () => {
  const isFiatMode = useSendStore((state) => state.isFiatMode)
  const fiatAmount = useSendStore((state) => state.fiatAmount)
  const cryptoAmount = useSendStore((state) => state.cryptoAmount)
  const setCryptoAmount = useSendStore((state) => state.setCryptoAmount)
  const setFiatAmount = useSendStore((state) => state.setFiatAmount)
  const shakeComponent = useSendStore((state) => state.shakeComponent)
  const selectedCurrency = useSendStore((state) => state.selectedCurrency)
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  // Получаем курс валют из API
  const {
    data: onRampPricePerUnit,
    isLoading: isLoadingOnRampPrice,
    isFetching: isFetchingOnRampPrice,
  } = useGetOnRampPricePerUnit({
    crypto: (selectedCurrency?.code as CryptoCode) || 'USDT',
    fiat: (displayCurrency?.isin as FiatCode) || 'USD',
  })

  // Получаем данные кошелька для fallback курса
  const walletCurrencyData = useGetWalletDataByCryptoCode({
    cryptoCode: selectedCurrency?.code || 'USDT',
  })

  // Используем курс из API, если доступен, иначе fallback на курс из кошелька
  const cryptoPricePerUnit =
    Number(onRampPricePerUnit?.amount) || walletCurrencyData?.pricePerUnit || 0

  // Проверяем состояние загрузки - показываем загрузку если данные еще не загружены
  const isLoading = isLoadingOnRampPrice || isFetchingOnRampPrice

  const adaptedAmount = isFiatMode ? fiatAmount : cryptoAmount

  // Максимальная длина ввода
  const MAX_LENGTH = 12

  // Обработка нажатия на цифру
  const handleNumberPress = (digit: string) => {
    const currentAmount = adaptedAmount.amount

    // Проверяем максимальную длину
    if (currentAmount.length >= MAX_LENGTH) {
      shakeComponent()
      return
    }

    // Если текущая сумма "0", заменяем на цифру
    if (currentAmount === '0') {
      updateAmount(digit)
      return
    }

    // Добавляем цифру к существующей сумме
    updateAmount(currentAmount + digit)
  }

  // Обработка нажатия на точку
  const handleDecimalPress = () => {
    const currentAmount = adaptedAmount.amount

    // Проверяем, есть ли уже точка
    if (currentAmount.includes('.')) {
      shakeComponent()
      return
    }

    // Проверяем максимальную длину
    if (currentAmount.length >= MAX_LENGTH) {
      shakeComponent()
      return
    }

    // Если пустая строка или "0", ставим "0."
    if (!currentAmount || currentAmount === '0') {
      updateAmount('0.')
      return
    }

    // Добавляем точку
    updateAmount(currentAmount + '.')
  }

  // Обработка backspace
  const handleBackspace = () => {
    const currentAmount = adaptedAmount.amount

    if (currentAmount.length <= 1) {
      updateAmount('0')
      return
    }

    updateAmount(currentAmount.slice(0, -1))
  }

  // WHEN THE CURRENCY ON INPUT IS FIAT
  const handleFiatInputChange = (inputAmount: string) => {
    const fiatAmount = inputAmount || '0'
    setFiatAmount({
      amount: fiatAmount,
      code: (displayCurrency?.isin as FiatCode) || 'USD',
    })

    if (!cryptoPricePerUnit) {
      // If no price available, set crypto amount to 0
      setCryptoAmount({
        amount: '0',
        code: selectedCurrency?.code || 'USDT',
      })
      return
    }

    const relativeInCrypto = fiatToCrypto({
      relativeFiatAmount: fiatAmount,
      cryptoPricePerUnitInFiat: cryptoPricePerUnit,
    })

    setCryptoAmount({
      amount: relativeInCrypto,
      code: selectedCurrency?.code || 'USDT',
    })
  }

  // WHEN THE CURRENCY ON INPUT IS CRYPTO
  const handleCryptoInputChange = (inputAmount: string) => {
    const cryptoAmount = inputAmount || '0'
    const amountParsed = parseAmountToFloat({
      amount: cryptoAmount,
      decimalSeparator: '.',
    })

    setCryptoAmount({
      amount: cryptoAmount,
      code: selectedCurrency?.code || 'USDT',
    })

    if (!cryptoPricePerUnit) {
      // If no price available, set fiat amount to 0
      setFiatAmount({
        amount: '0',
        code: (displayCurrency?.isin as FiatCode) || 'USD',
      })
      return
    }

    const relativeInFiat = cryptoToFiat({
      amountCrypto: amountParsed,
      cryptoPricePerUnit,
    })

    setFiatAmount({
      amount: relativeInFiat,
      code: (displayCurrency?.isin as FiatCode) || 'USD',
    })
  }

  // Обновление суммы в зависимости от режима
  const updateAmount = (newAmount: string) => {
    if (!selectedCurrency?.code) {
      return
    }

    if (isFiatMode) {
      handleFiatInputChange(newAmount)
    } else {
      handleCryptoInputChange(newAmount)
    }
  }

  // Данные для кнопок клавиатуры
  const keypadData = [
    [
      { label: '1', value: '1' },
      { label: '2', value: '2' },
      { label: '3', value: '3' },
    ],
    [
      { label: '4', value: '4' },
      { label: '5', value: '5' },
      { label: '6', value: '6' },
    ],
    [
      { label: '7', value: '7' },
      { label: '8', value: '8' },
      { label: '9', value: '9' },
    ],
    [
      { label: '•', value: '.' },
      { label: '0', value: '0' },
      { label: 'backspace', value: 'backspace' },
    ],
  ]

  // Обработка нажатия на кнопку
  const handleButtonPress = (value: string) => {
    if (value === 'backspace') {
      handleBackspace()
    } else if (value === '.') {
      handleDecimalPress()
    } else {
      handleNumberPress(value)
    }
  }

  return (
    <BaseView className="px-6 flex-1 flex ">
      {keypadData.map((row, rowIndex) => (
        <BaseView key={rowIndex} row justify="space-between" className="flex-1">
          {row.map((button) => (
            <BaseView
              key={button.value}
              center
              className={`transition-transform duration-150 select-none flex-1 mx-2 ${
                isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-95'
              }`}
              style={{
                // minHeight: 48, // Минимальная высота кнопки
                borderRadius: 24, // Адаптивный радиус
                pointerEvents: isLoading ? 'none' : 'auto',
              }}
              onClick={() => !isLoading && handleButtonPress(button.value)}
            >
              {button.value === 'backspace' ? (
                <AppIcons name="arrowLeft" size={22} color="white" />
              ) : (
                <BaseText
                  fontSize={button.value === '0' ? 26 : 22} // Уменьшил с 32/28 до 26/22
                  fontFamily="bold"
                  color="white"
                  style={{
                    lineHeight: 1,
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {button.label}
                </BaseText>
              )}
            </BaseView>
          ))}
        </BaseView>
      ))}
    </BaseView>
  )
}
